import { useAcquisition, useAdminJobs, usePlatformStats } from "../../api/queries";
import type { AcquisitionRow } from "../../api/types";
import { Card, Empty, ErrorNote, Loading, Stat } from "../../ui/primitives";
import { JobStatusBadge } from "../site/OverviewPage";

/** Signups by where they came from, over the window the marketing plan reviews. */
const ACQUISITION_DAYS = 90;

/**
 * One table per dimension. The columns are a funnel, so each is read against
 * the one before it: ten signups and no connected stores is a channel sending
 * the wrong people, not a small channel.
 */
function AcquisitionTable({ caption, rows }: { caption: string; rows: AcquisitionRow[] }) {
  if (rows.length === 0) return <Empty title="No signups in this period" />;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <caption className="mb-2 text-left text-xs font-medium text-muted">{caption}</caption>
        <thead>
          <tr className="text-left text-xs text-muted">
            <th scope="col" className="py-1 pr-3 font-medium">
              Source
            </th>
            <th scope="col" className="py-1 pr-3 text-right font-medium">
              Signed up
            </th>
            <th scope="col" className="py-1 pr-3 text-right font-medium">
              Connected a store
            </th>
            <th scope="col" className="py-1 pr-3 text-right font-medium">
              Had conversations
            </th>
            <th scope="col" className="py-1 text-right font-medium">
              Paying
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {rows.map((row) => (
            <tr key={row.label}>
              <th scope="row" className="py-1.5 pr-3 text-left font-medium">
                {row.label}
              </th>
              <td className="py-1.5 pr-3 text-right tabular-nums">{row.signups}</td>
              <td className="py-1.5 pr-3 text-right tabular-nums">{row.connected}</td>
              <td className="py-1.5 pr-3 text-right tabular-nums">{row.conversing}</td>
              <td className="py-1.5 text-right tabular-nums">{row.paying}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AcquisitionCard() {
  const report = useAcquisition(ACQUISITION_DAYS);

  return (
    <Card title={`Where signups came from — last ${ACQUISITION_DAYS} days`}>
      {report.isLoading ? (
        <Loading />
      ) : report.error ? (
        <ErrorNote error={report.error} />
      ) : (
        <div className="space-y-6">
          <AcquisitionTable
            caption="Tracked: campaign tag, else the referring site"
            rows={report.data?.by_channel ?? []}
          />
          <AcquisitionTable
            caption="Self-reported: “How did you hear about us?”"
            rows={report.data?.by_answer ?? []}
          />
        </div>
      )}
    </Card>
  );
}

export function AdminOverviewPage() {
  const stats = usePlatformStats();
  // Failures only: an operator opening this page wants the exceptions, and a
  // list of successful syncs would bury them.
  const failing = useAdminJobs("failed");

  if (stats.isLoading) return <Loading />;
  if (stats.error) return <ErrorNote error={stats.error} />;

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Organizations" value={stats.data?.organizations ?? 0} />
        <Stat label="Active" value={stats.data?.active_organizations ?? 0} />
        <Stat label="Users" value={stats.data?.users ?? 0} />
      </div>

      <AcquisitionCard />

      <Card title="Failing syncs">
        {failing.isLoading ? (
          <Loading />
        ) : (failing.data ?? []).length === 0 ? (
          <Empty title="Nothing is failing" />
        ) : (
          <ul className="divide-y divide-line">
            {(failing.data ?? []).slice(0, 10).map((job) => (
              <li key={job.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                <JobStatusBadge status={job.status} />
                <span className="font-medium">{job.organization_name}</span>
                <span className="text-muted">{job.site_name}</span>
                {job.error && <span className="w-full text-xs text-bad">{job.error}</span>}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
